import { useState } from "react";
import * as z from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword, signOut, reload } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "@/lib/firebase";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";

export function LoginForm() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Zod schema with translations
  const formSchema = z.object({
    email: z.string()
      .email(t("validation.invalidEmail"))
      .min(1, t("validation.required")),
    password: z.string().min(1, t("validation.required")),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsLoading(true);
    try {
      // Sign in with email and password
      const userCredential = await signInWithEmailAndPassword(
        auth,
        values.email,
        values.password
      );

      let user = userCredential.user;

      // Reload user to get latest verification status
      try {
        await reload(user);
        user = auth.currentUser!;
        console.log(`[Login] After reload - emailVerified: ${user.emailVerified}`);
      } catch (error) {
        console.error("[Login] Error reloading user:", error);
      }

      // Check if email is verified
      if (!user.emailVerified) {
        // Email not verified - show warning
        toast({
          title: t("login.emailNotVerified"),
          description: t("login.pleaseVerifyEmail"),
          variant: "destructive",
        });
        
        // Sign out user
        await signOut(auth);
        setIsLoading(false);
        return;
      }

      // Email is verified - call Cloud Function to update Firestore
      try {
        const updateEmailVerification = httpsCallable(functions, "onEmailVerified");
        await updateEmailVerification();
        console.log("✓ Email verification status updated via Cloud Function");
      } catch (verifyError) {
        console.error("Error updating verification status:", verifyError);
        // Continue anyway - user can still login
      }

      toast({
        title: t("login.success") || "Success",
        description: t("login.welcome") || "Welcome back!",
      });

      // Redirect to success page
      setTimeout(() => {
        navigate("/login-success");
      }, 1000);
    } catch (error: any) {
      console.error("Login error:", error);
      const errorCode = error.code || "default";
      toast({
        title: t("errors.default"),
        description: t(`errors.${errorCode}`, { defaultValue: t("errors.default") }),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Card className="w-full shadow-lg border-primary/10">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold text-center text-primary">
          {t("login.title")}
        </CardTitle>
        <CardDescription className="text-center">
          {t("login.subtitle")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Email Field */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("login.email")}</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder={t("register.emailPlaceholder")}
                      autoComplete="email"
                      dir="ltr"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Password Field */}
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("login.password")}</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder={t("register.passwordPlaceholder")}
                        autoComplete="current-password"
                        {...field}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute end-0 top-0 h-full px-3 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeSlashIcon className="h-5 w-5 text-muted-foreground" />
                        ) : (
                          <EyeIcon className="h-5 w-5 text-muted-foreground" />
                        )}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Forgot Password Link */}
            <div className="text-end">
              <a href="/reset-password" className="text-sm text-primary hover:underline">
                {t("login.forgotPassword")}
              </a>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-primary to-secondary hover:opacity-90"
              size="lg"
              disabled={isLoading}
            >
              {isLoading ? t("common.loading") : t("login.signIn")}
            </Button>
          </form>
        </Form>

        {/* Sign Up Link */}
        <div className="mt-6 text-center text-sm">
          <span className="text-muted-foreground">{t("login.noAccount")}</span>{" "}
          <a href="/register" className="font-semibold text-primary hover:underline">
            {t("login.createAccount")}
          </a>
        </div>
      </CardContent>
    </Card>
  );
}
